import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  Skeleton,
  Tooltip,
  Pagination,
  TextField,
  Chip,
  Grid,
  InputAdornment,
  InputBase,
  IconButton,
  Paper,
  Divider,
} from "@mui/material";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import SearchIcon from "@mui/icons-material/Search";
import PlaceIcon from "@mui/icons-material/Place";
import CategoryIcon from "@mui/icons-material/Category";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import EmptyState from "../../../shared/ui/EmptyState";

const API_URL = "http://localhost:5000/api/packages";

const CardSkeleton = () => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 2,
      overflow: "hidden",
      border: "1px solid",
      borderColor: "divider",
    }}
  >
    <Box sx={{ position: "relative", paddingTop: "66.67%", bgcolor: "action.hover" }}>
      <Skeleton
        variant="rectangular"
        sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
      />
    </Box>
    <CardContent sx={{ p: 2 }}>
      <Skeleton variant="text" width="88%" height={20} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1.5 }} />
      <Skeleton variant="text" width="45%" height={16} sx={{ mb: 2 }} />
      <Divider sx={{ mb: 1.5 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Skeleton variant="text" width={90} height={28} />
        <Skeleton variant="rounded" width={60} height={30} />
      </Stack>
    </CardContent>
  </Card>
);

const TourListings = () => {
  const [tours, setTours] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(9);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("");
  const navigate = useNavigate();

  const fetchRules = async () => {
    const distributor = JSON.parse(localStorage.getItem("authUser"));
    const token = localStorage.getItem("token");
    if (!distributor?._id || !token) { setPricingRules([]); return; }
    try {
      const res = await fetch(`http://localhost:5000/api/pricing-rules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setPricingRules(Array.isArray(data) ? data : []);
    } catch { setPricingRules([]); }
  };

  useEffect(() => {
    const fetchPackages = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams({
          page, limit,
          ...(search && { search }),
          ...(location && { location }),
          ...(category && { category }),
        });
        const response = await fetch(`${API_URL}?${queryParams.toString()}`);
        const result = await response.json();
        setTours(
          result.data.map((pkg) => ({
            id: pkg._id,
            title: pkg.basicInfo.tour_name,
            category: pkg.basicInfo.tour_type,
            currency: pkg.pricing.currency || "",
            basePrice: pkg.pricing.pricePerPerson,
            duration: pkg.basicInfo.duration,
            location: [pkg.basicInfo.city, pkg.basicInfo.country].filter(Boolean).join(", "),
            image: pkg.media?.tourImages?.[0] || "",
            owner: pkg.createdBy
              ? { name: pkg.createdBy.displayName, company: pkg.createdBy.company }
              : null,
          }))
        );
        setTotalPages(result.totalPages ?? 1);
      } catch { setTours([]); }
      finally { setLoading(false); }
    };
    fetchPackages();
    fetchRules();
  }, [page, limit, search, location, category]);

  const getRulesForTour = (tourId) =>
    Array.isArray(pricingRules)
      ? pricingRules.filter((rule) => {
          if (!rule?.package) return false;
          const pid = typeof rule.package === "object" ? rule.package._id : rule.package;
          return pid === tourId;
        })
      : [];

  const hasFilters = search || location || category;
  const clearFilters = () => { setSearch(""); setLocation(""); setCategory(""); setPage(1); };

  return (
    <Box sx={{ bgcolor: "#F7F8FC", minHeight: "100vh" }}>
      {/* Page header */}
      <Box sx={{ bgcolor: "white", borderBottom: "1px solid", borderColor: "divider", px: { xs: 2, md: 5 }, pt: 3.5, pb: 2.5 }}>
        <Typography variant="h5" fontWeight={800} sx={{ mb: 0.4, letterSpacing: -0.3 }}>
          Explore Tour Packages
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {loading
            ? "Loading experiences..."
            : tours.length > 0
            ? `${tours.length} experience${tours.length !== 1 ? "s" : ""} available`
            : hasFilters
            ? "No experiences match your filters"
            : "No experiences available yet"}
        </Typography>
      </Box>

      {/* Sticky search + filter bar */}
      <Box
        sx={{
          bgcolor: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
          px: { xs: 2, md: 5 },
          py: 2,
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
          {/* Rounded search bar */}
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              maxWidth: { sm: 380 },
              border: "1.5px solid",
              borderColor: "divider",
              borderRadius: 99,
              px: 2,
              py: 0.6,
              transition: "border-color 0.2s",
              "&:focus-within": { borderColor: "#E50000" },
            }}
          >
            <SearchIcon sx={{ color: "text.disabled", fontSize: 19, mr: 1, flexShrink: 0 }} />
            <InputBase
              placeholder="Search experiences..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              sx={{ flex: 1, fontSize: "0.875rem" }}
            />
            {search && (
              <IconButton size="small" onClick={() => { setSearch(""); setPage(1); }} sx={{ p: 0.25, ml: 0.5 }}>
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            )}
          </Paper>

          {/* Location filter pill */}
          <TextField
            size="small"
            placeholder="Location"
            value={location}
            onChange={(e) => { setLocation(e.target.value); setPage(1); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PlaceIcon sx={{ fontSize: 16, color: location ? "#E50000" : "text.disabled" }} />
                </InputAdornment>
              ),
              endAdornment: location ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setLocation(""); setPage(1); }} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              minWidth: 150,
              "& .MuiOutlinedInput-root": {
                borderRadius: 99,
                fontSize: "0.85rem",
                ...(location && {
                  "& fieldset": { borderColor: "#E50000" },
                  "&:hover fieldset": { borderColor: "#E50000" },
                }),
              },
            }}
          />

          {/* Category filter pill */}
          <TextField
            size="small"
            placeholder="Category"
            value={category}
            onChange={(e) => { setCategory(e.target.value); setPage(1); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CategoryIcon sx={{ fontSize: 16, color: category ? "#E50000" : "text.disabled" }} />
                </InputAdornment>
              ),
              endAdornment: category ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setCategory(""); setPage(1); }} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              minWidth: 145,
              "& .MuiOutlinedInput-root": {
                borderRadius: 99,
                fontSize: "0.85rem",
                ...(category && {
                  "& fieldset": { borderColor: "#E50000" },
                  "&:hover fieldset": { borderColor: "#E50000" },
                }),
              },
            }}
          />

          {hasFilters && (
            <Button
              variant="text"
              size="small"
              onClick={clearFilters}
              sx={{ color: "text.secondary", fontSize: "0.8rem", whiteSpace: "nowrap", minWidth: "auto" }}
            >
              Clear all
            </Button>
          )}
        </Stack>
      </Box>

      {/* Grid content */}
      <Box sx={{ px: { xs: 2, md: 5 }, py: 4 }}>
        {loading ? (
          <Grid container spacing={2.5}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <CardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : tours.length === 0 ? (
          <Box sx={{ py: 8 }}>
            <EmptyState
              icon={hasFilters ? <SearchOffIcon /> : <ExploreOutlinedIcon />}
              title={hasFilters ? "No results found" : "No tours available"}
              description={
                hasFilters
                  ? "Try different search terms or clear your filters."
                  : "No packages have been listed yet. Check back soon."
              }
              action={
                hasFilters ? (
                  <Button variant="outlined" onClick={clearFilters}>Clear filters</Button>
                ) : undefined
              }
            />
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {tours.map((tour, index) => {
              const rules = getRulesForTour(tour.id);
              return (
                <Grid item xs={12} sm={6} md={4} key={tour.id} sx={{ display: "flex" }}>
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: index * 0.04 }}
                    style={{ display: "flex", flex: 1 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        overflow: "hidden",
                        cursor: "pointer",
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "white",
                        transition: "box-shadow 0.2s ease, transform 0.2s ease",
                        "&:hover": {
                          boxShadow: "0 6px 24px rgba(0,0,0,0.1)",
                          transform: "translateY(-3px)",
                        },
                      }}
                      onClick={() => navigate(`/package/${tour.id}`)}
                    >
                      {/* 3:2 aspect-ratio image */}
                      <Box sx={{ position: "relative", paddingTop: "66.67%", bgcolor: "#f0f2f5" }}>
                        {tour.image ? (
                          <Box
                            component="img"
                            src={tour.image}
                            alt={tour.title}
                            onError={(e) => { e.target.style.display = "none"; }}
                            sx={{
                              position: "absolute",
                              top: 0, left: 0,
                              width: "100%", height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0, left: 0, right: 0, bottom: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <ExploreOutlinedIcon sx={{ fontSize: 52, color: "rgba(0,0,0,0.12)" }} />
                          </Box>
                        )}

                        {/* Tour type badge */}
                        {tour.category && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 10,
                              left: 10,
                              bgcolor: "rgba(0,0,0,0.6)",
                              color: "white",
                              px: 1.25,
                              py: 0.35,
                              borderRadius: 1,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              backdropFilter: "blur(6px)",
                              letterSpacing: 0.3,
                              textTransform: "uppercase",
                            }}
                          >
                            {tour.category}
                          </Box>
                        )}

                        {/* Pricing rules indicator */}
                        {rules.length > 0 && (
                          <Tooltip
                            title={`${rules.length} pricing rule${rules.length > 1 ? "s" : ""} applied to this tour`}
                            arrow
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                bgcolor: "rgba(229,0,0,0.88)",
                                color: "white",
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backdropFilter: "blur(4px)",
                              }}
                            >
                              <TuneIcon sx={{ fontSize: 14 }} />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>

                      {/* Card content */}
                      <CardContent
                        sx={{
                          p: 2,
                          pb: "16px !important",
                          flexGrow: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* Title */}
                        <Typography
                          fontWeight={700}
                          sx={{
                            fontSize: "0.93rem",
                            lineHeight: 1.4,
                            mb: 0.5,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {tour.title}
                        </Typography>

                        {/* Operator */}
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.25 }}>
                          {tour.owner?.name || "Unknown Operator"}
                          {tour.owner?.company ? ` · ${tour.owner.company}` : ""}
                        </Typography>

                        {/* Location + Duration */}
                        <Stack direction="row" spacing={2} sx={{ mb: "auto" }} flexWrap="wrap">
                          {tour.location && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <PlaceIcon sx={{ fontSize: 12, color: "text.disabled" }} />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{ maxWidth: 130 }}
                              >
                                {tour.location}
                              </Typography>
                            </Stack>
                          )}
                          {tour.duration && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <AccessTimeIcon sx={{ fontSize: 12, color: "text.disabled" }} />
                              <Typography variant="caption" color="text.secondary">
                                {tour.duration} {Number(tour.duration) === 1 ? "day" : "days"}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>

                        {/* Price + CTA */}
                        <Box
                          sx={{
                            mt: 2,
                            pt: 1.5,
                            borderTop: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", lineHeight: 1.2 }}
                            >
                              From
                            </Typography>
                            <Typography
                              fontWeight={800}
                              sx={{ fontSize: "1rem", lineHeight: 1.2, color: "text.primary" }}
                            >
                              {tour.currency} {Number(tour.basePrice).toLocaleString()}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ lineHeight: 1.2 }}
                            >
                              per person
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/package/${tour.id}`);
                            }}
                            sx={{
                              bgcolor: "#E50000",
                              "&:hover": { bgcolor: "#C00000" },
                              borderRadius: 1.5,
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              px: 2,
                              py: 0.75,
                              flexShrink: 0,
                            }}
                          >
                            View
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Pagination */}
        {!loading && tours.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TourListings;
